import React, { useState } from 'react';
import { useAuth } from './context/auth/AuthContext';
import CognitoAuthService from './services/CognitoAuthService';
import ForgotPassword from './components/ForgotPassword';
import { loadStripe } from '@stripe/stripe-js';
import { API_BASE_URL } from './config';

function Login({ isOpen, onClose, onSwitchToSignUp }) {
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentSetupLoading, setPaymentSetupLoading] = useState(false);

  const cognitoService = new CognitoAuthService();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await cognitoService.loginUser(formData.email, formData.password);
      
      // 从ID token中获取用户信息
      const tokenPayload = JSON.parse(atob(result.idToken.split('.')[1]));
      const userName = tokenPayload.name || tokenPayload['custom:full_name'] || formData.email;
      const userEmail = tokenPayload.email || formData.email;
      
      alert('Login successful!');
      await login(userName, userEmail);
      
      // 存储token到localStorage
      localStorage.setItem('cognitoTokens', JSON.stringify({
        accessToken: result.accessToken,
        idToken: result.idToken,
        refreshToken: result.refreshToken
      }));
      
      onClose();
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Login failed');
      alert('Login failed: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };
  
  const handleClose = () => {
    setFormData({ email: '', password: '' });
    setShowPassword(false);
    setError('');
    onClose();
  };

  const handleSwitchToSignUp = () => {
    handleClose();
    onSwitchToSignUp();
  };

  const handleSwitchToForgotPassword = () => {
    setShowForgotPassword(true);
  };

  // Function to create payment setup session (copied from SignUp.jsx)
  const createPaymentSetupSession = async (userEmail, userName) => {
    try {
      setPaymentSetupLoading(true);
      
      const response = await fetch(`${API_BASE_URL}/api/create-payment-setup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userEmail: userEmail,
          userName: userName
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create payment setup session');
      }

      const { sessionId } = await response.json();
      
      // Redirect to Stripe Checkout for payment setup
      const stripe = await loadStripe('pk_live_51RMOUfJZKrN6X9pNIE0K4ADsWn0K1t2zovlkrnmKQIcA0bHhNs141ofA1UlX2ctp0OWZvv5tSEVZG7kLKVKKYCuu00ZMM27ciP');
      const { error } = await stripe.redirectToCheckout({ sessionId });
      
      if (error) {
        console.error('Error redirecting to payment setup:', error);
        alert('Error setting up payment method. You can set it up later from your profile.');
      }
    } catch (error) {
      console.error('Error creating payment setup session:', error);
      alert('Error setting up payment method. You can set it up later from your profile.');
    } finally {
      setPaymentSetupLoading(false);
    }
  };

  /* Login with Google - commented out
  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');

    try {
      const result = await cognitoService.initiateGoogleSignIn();
      
      // 🔍 DEBUG: 检查后端返回的完整数据
      console.log('🔍 Complete Google OAuth Response:', result);
      console.log('🔍 User object:', result.user);
      console.log('🔍 isNewUser:', result.isNewUser);
      
      // Extract user info from the result
      // For new users, user.email might be empty, so we need to get email from other sources
      let userEmail = result.user?.email || '';
      let userName = result.user?.full_name || result.user?.email || 'Google User';
      
      // If userEmail is empty, try to extract from ID token or access token
      if (!userEmail && result.idToken) {
        try {
          const tokenPayload = JSON.parse(atob(result.idToken.split('.')[1]));
          userEmail = tokenPayload.email || tokenPayload.sub || '';
          if (!userName || userName === 'Google User') {
            userName = tokenPayload.name || tokenPayload.email || 'Google User';
          }
          console.log('🔍 Extracted from ID token:', { userEmail, userName });
        } catch (error) {
          console.log('⚠️ Could not parse ID token:', error);
        }
      }
      
      // Check if this is a new user - if userEmail is still empty, definitely new user
      const isNewUser = result.isNewUser || !userEmail;
      
      // 🧪 Test Log: 前端收到的用户状态
      console.log(`🧪 Frontend Result: ${isNewUser ? 'NEW USER' : 'EXISTING USER'} - ${userEmail}`);
      await login(userName, userEmail);
      
      // Store tokens
      localStorage.setItem('cognitoTokens', JSON.stringify({
        accessToken: result.accessToken,
        idToken: result.idToken,
        refreshToken: result.refreshToken
      }));
      
      // If Stripe setup intent is available, store it
      if (result.stripe?.setup_intent_client_secret) {
        console.log('Stripe setup intent available for user');
        
        // Store setup intent for potential future use
        localStorage.setItem('stripeSetupIntent', JSON.stringify({
          client_secret: result.stripe.setup_intent_client_secret,
          customer_id: result.stripe.customer_id,
          user_email: userEmail
        }));
      }
      
      // For new Google users, redirect to payment setup (same as SignUp.jsx)
      if (isNewUser) {
        console.log('🧪 NEW USER detected - redirecting to payment setup');
        
        // If userEmail is still empty, we need to get it from user
        if (!userEmail) {
          const email = prompt('Please enter your email address to complete registration:');
          if (!email) {
            alert('Email is required to complete registration.');
            return;
          }
          userEmail = email;
          console.log('📧 User provided email:', userEmail);
        }
        
        // Use the same logic as SignUp.jsx - always redirect to Stripe Checkout
        alert('Welcome! Please set up your payment method to complete registration.');
        
        onClose();
        
        setTimeout(async () => {
          await createPaymentSetupSession(userEmail, userName);
        }, 500);
        
      } else {
        // Existing user - just close and continue
        console.log('🧪 EXISTING USER detected - no payment setup needed');
        alert('Welcome back! Google Sign-In successful.');
        onClose();
      }
      
    } catch (err) {
      console.error('Google Sign-In error:', err);
      setError(err.message || 'Google Sign-In failed');
      alert('Google Sign-In failed: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };
  */

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content auth-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header auth-header">
          <button className="close-button-top" onClick={handleClose}>×</button>
          <h2 className="modal-title auth-title">Login</h2>
        </div>
        
        <form onSubmit={handleSubmit} className="auth-form">
          {error && (
            <div className="error-message" style={{ color: 'red', marginBottom: '10px', textAlign: 'center' }}>
              {error}
            </div>
          )}
          
          <div className="form-group">
            <div className="input-with-label">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email"
                required
                disabled={loading}
              />
            </div>
          </div>
          
          <div className="form-group">
            <div className="input-with-label">
              <label htmlFor="password">Password</label>
              <div className="password-input-container">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Enter your password"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                >
                  <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>
            </div>
          </div>
          
          <div className="forgot-password-section">
            <span className="forgot-password" onClick={handleSwitchToForgotPassword} style={{ cursor: 'pointer' }}>
              Forgot Password?
            </span>
          </div>
          
          <div className="form-actions">
            <button type="submit" className="auth-submit-button" disabled={loading}>
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </div>
          
          {/* Login with Google - commented out
          <div className="divider-section">
            <div className="divider-line"></div>
            <span className="divider-text">or</span>
            <div className="divider-line"></div>
          </div>
          
          <div className="google-signin-section">
            <button 
              type="button" 
              className="google-signin-button" 
              onClick={handleGoogleSignIn}
              disabled={loading || paymentSetupLoading}
            >
              <svg className="google-icon" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {paymentSetupLoading ? 'Setting up payment...' : loading ? 'Signing in...' : 'Continue with Google'}
            </button>
          </div>
          */}
          
          <div className="auth-footer">
            <p>Don't have an account? <span className="link-text" onClick={handleSwitchToSignUp}>Sign up</span></p>
          </div>
        </form>
      </div>
      
      <ForgotPassword 
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
        onSwitchToLogin={() => setShowForgotPassword(false)}
      />
    </div>
  );
}

export default Login; 